import styled from 'styled-components';
import { useSearchParams } from 'next/navigation';
import { Button, Placeholder, Text } from '@lmn/design-system';
import { getTotalPerNightsLabel } from '@/app/components/search/components/Card/utils/getPriceDetails';
import { TaxesAllIncluded } from '@/app/components/hotel-detail/PackageDetails';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import { useOffersContext } from '@/app/components/hotel-detail/OffersProvider';
import { getMealPlanCode } from '@/app/components/hotel-detail/rooms/roomCommon';
import { useCommonClient } from '@/app/common/commonClientProvider';
import { useSelectedMealPlan } from '@/app/components/hotel-detail/SelectedMealPlanProvider';
import { useTranslation } from '@/app/components/translations/Translation';
import { RoomPrice } from '@/app/components/hotel-detail/rooms/RoomPrice';
import { LocalTaxes, StyledPriceTypeContainer } from '@/app/components/hotel-detail/rooms/LocalTaxes';
import useUserConfig from '@/app/components/hotel-detail/hooks/useUserConfig';
import { useUrlSearchParams } from '@/app/components/hotel-detail/UrlSearchParamsProvider';
import { useEvoContinueFromRooms } from '@/app/components/hotel-detail/evo/hooks/useEvoContinueFromRooms';
import { useTrackEvoBookClicked } from '@/app/components/hotel-detail/evo/hooks/useTrackEvoBookClicked';
import { mergeBrandedFareIntoMealPlan } from '@/app/components/hotel-detail/lib/brandedFarePricing';
import { URLParameters } from '@/app/common/urlParameters';
import { PAGE_TYPE } from '@/app/common/pageType';

const PriceSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PriceAndBookButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  align-items: flex-end;
`;

// The price takes whatever the button leaves free; min-width: 0 lets its taxes line wrap
// inside the column instead of setting a nowrap min-content floor that pushes the row
// wider than the fixed 423px sidebar and the CTA out of the card (MPH-687).
const PriceContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;

  /* TaxesAllIncluded renders its trigger label nowrap (SLIDERS-2168, sized for the roomier
     non-EVO summary); in this column a long localized line must wrap — its info icon stays
     glued to the last word (see AddElementToLastWordInString), so it never wraps alone. */
  > div[role='button'] {
    white-space: normal;
  }
`;

// Natural label width, capped at half the row (calc offsets the 16px gap) — the label wraps
// beyond the cap. The previous nowrap painted long localized labels ("Rezervovat hned teď")
// outside the button box, clipped by the card (MPH-687).
const StyledButton = styled(Button)`
  min-height: 48px;
  padding: 12px 16px;
  flex: 0 0 auto;
  max-width: calc(50% - 8px);
`;

export function PriceAndBookSection(props: { showChangeTransportButton?: boolean }) {
  const { showChangeTransportButton = false } = props;
  const { localization } = usePageWrapperData();
  const { accommodationOnly, nights, totalGuestsWithoutInfants } = useUrlSearchParams();
  const { selectedMealPlan, selectedRoom, selectedBrandedFare } = useSelectedMealPlan();
  const displayMealPlan = selectedMealPlan
    ? mergeBrandedFareIntoMealPlan(selectedMealPlan, selectedBrandedFare)
    : undefined;
  const { offersData, isLoadingRooms } = useOffersContext();
  const { t } = useTranslation();
  const { isDoublePrice, isDoublePriceDP } = useUserConfig();
  const taxesIncludedPopup = localization?.configValuesApp?.taxesIncludedPopup || false;
  const taxesIncludedDoublePricing = localization?.configValuesApp?.taxesIncludedDoublePricing || false;
  // Taxes-included markets hide the plain excluded-charges line and show the
  // clickable "all taxes included" label with its breakdown modal instead,
  // mirroring the non-EVO package summary (PackageDetails).
  const taxesAllIncluded = taxesIncludedPopup || taxesIncludedDoublePricing;
  const { handleEvoGoToCheckout } = useCommonClient();
  const continueFromRooms = useEvoContinueFromRooms();
  const trackEvoBookClicked = useTrackEvoBookClicked();
  // MPH-913 / MPH-935: the copy follows the CTA's destination, not the page it sits on.
  // - goes to checkout (the DP flight page, and the Hotel Only detail page — Hotel Only has no flight
  //   step, so the rooms CTA is the last one before checkout): "Book now". That is also the key legacy
  //   Hotel Only already uses, so cux_4064's two arms show the same words.
  // - advances to another step (the DP detail page): cc.rooms.continue, the key the mobile room CTA
  //   already uses. Unlike vacation.select.details.book it reads as "continue" in every market — the
  //   old key means "book" in da/fi/hu/nl/no/pl/ro/sv.
  // - cux_4062 variant T swaps the flight page's "Book now" for that same "Continue" copy. It is a
  //   DP-only test, so the config resolves false on Hotel Only; the isFlightPage guard says so outright.
  // The step comes from the page type rather than from showChangeTransportButton: that prop
  // defaults to false, so any future call site omitting it would silently inherit the flight copy.
  const searchParams = useSearchParams();
  const isFlightPage = searchParams.get(URLParameters.PAGE_TYPE) === PAGE_TYPE.TRANSPORT_DETAIL;
  const flightPageContinueCopy = isFlightPage && (localization?.configValuesApp?.flightPageCtaContinueCopy || false);
  const goesToCheckout = isFlightPage || accommodationOnly;
  const bookCtaKey =
    goesToCheckout && !flightPageContinueCopy ? 'vacation.select.details.book.b' : 'vacation.select.cc.rooms.continue';

  const onBookClick = async () => {
    if (selectedMealPlan && selectedRoom) {
      trackEvoBookClicked();

      handleEvoGoToCheckout({
        roomOfferId: selectedRoom.roomOfferId,
        mealPlanId: selectedMealPlan.id,
        mealPlanCode: getMealPlanCode(selectedMealPlan),
        transportId: offersData?.transport?.transports[0]?.id,
        brandedFareId: selectedBrandedFare?.fareId,
      });
    }
  };

  return selectedRoom && displayMealPlan && !isLoadingRooms ? (
    <PriceSection>
      <PriceAndBookButtonContainer>
        <PriceContainer>
          <RoomPrice
            hasFlashSalePromo={false}
            showMyPriceColor={false}
            mealPlan={displayMealPlan}
            roomsIncludedInPrice={selectedRoom.roomsIncludedInPrice}
          />
          {accommodationOnly && (
            <Text variant="Caption" weight="bold">
              {getTotalPerNightsLabel({ nights: offersData?.nights || nights, t })}
            </Text>
          )}
          <LocalTaxes
            css={`
              &:empty {
                display: none;
              }

              // LocalTaxes defaults to right-aligned (justify-content: flex-end) for the search
              // cards; in the trip-overview price column the taxes/fees label must sit flush-left
              // under the price. The inner ExcludedChargesLabelContainer uses justify-content:
              // inherit, so overriding it here left-aligns the label.
              justify-content: flex-start;

              // Same wrap rationale as the TaxesAllIncluded override on PriceContainer: the
              // double-pricing total line is nowrap for the search cards, but here it must
              // wrap rather than widen the row past the sidebar.
              ${StyledPriceTypeContainer} {
                white-space: normal;
              }

              ${
                isDoublePrice || isDoublePriceDP
                  ? `
                  flex-direction: column;
                  `
                  : undefined
              }
            `}
            nights={offersData?.nights || nights}
            roomsIncludedInPrice={selectedRoom.roomsIncludedInPrice}
            excludedCharges={taxesAllIncluded ? undefined : displayMealPlan.excludedCharges}
            excludedChargesIncluded={displayMealPlan.excludedChargesIncluded}
            passengers={totalGuestsWithoutInfants}
            price={displayMealPlan.finalTotalPrice}
            priceWithoutTaxes={displayMealPlan.priceWithoutTaxes}
            showDoublePrice={isDoublePrice || isDoublePriceDP || accommodationOnly}
            showTotalPriceIncludedTaxes={true}
          />
          {taxesAllIncluded && (
            <TaxesAllIncluded mealPlan={displayMealPlan} isDoublePricing={taxesIncludedDoublePricing} />
          )}
        </PriceContainer>
        <StyledButton
          variant="primary"
          data-testid="continue-button"
          // The rooms step routes through the shared handler — it opens the cancellation-options modal
          // when the room has several, and picks the flight page or checkout by product type (MPH-935).
          // The flight page books directly, with the transport it is showing.
          onClick={showChangeTransportButton ? () => continueFromRooms() : onBookClick}
        >
          {t(bookCtaKey)}
        </StyledButton>
      </PriceAndBookButtonContainer>
    </PriceSection>
  ) : (
    // MPH-1144: while the rate loads, the CTA shows in its inactive state rather than as a skeleton.
    <PriceSection>
      <PriceAndBookButtonContainer>
        <PriceContainer
          css={`
            gap: 4px;
          `}
        >
          <Placeholder height="24px" width="96px" />
          <Placeholder height="16px" width="120px" />
        </PriceContainer>
        <StyledButton variant="primary" disabled={true}>
          {t(bookCtaKey)}
        </StyledButton>
      </PriceAndBookButtonContainer>
    </PriceSection>
  );
}
